import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, Umbrella, GitBranch, Clock, CheckCircle2, Circle, ArrowRight, ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, ShieldCheck, Info, Home, Globe } from 'lucide-react';
import { adminApi, employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { formatDays } from '@/lib/utils';

const STAT_ICONS = {
  'Active People': Users,
  'Leave Types': Umbrella,
  'Approval Chains': GitBranch,
  'Pending Requests': Clock,
};

export default function DashboardPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [stats, setStats] = useState({
    users: 0,
    leaveTypes: 0,
    chains: 0,
    pendingRequests: 0,
    companyName: 'LeavePortal',
    hasUsers: false,
    hasLeaveTypes: false,
    hasChains: false,
    hasHolidays: false,
    hasTemplates: false,
  });
  const [recentAudit, setRecentAudit] = useState([]);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [healthIssues, setHealthIssues] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        // One reports call does the heavy lifting (load all users + all
        // balances + all requests, slice into pendingCount + upcomingLeave +
        // alerts). Previously the dashboard fired three separate getReport
        // calls — each one re-scanned the same per-user balances and requests,
        // so the dashboard paid 3× the cost of a single load for no gain.
        const [usersRes, typesRes, chainsRes, settingsRes, auditRes, holidaysRes, templatesRes, dashboardRes] =
          await Promise.all([
            adminApi.listUsers().catch(() => ({ users: [] })),
            adminApi.listLeaveTypes().catch(() => ({ leaveTypes: [] })),
            adminApi.listChains().catch(() => ({ chains: [] })),
            adminApi.getSettings().catch(() => ({ settings: {} })),
            adminApi.listAudit().catch(() => ({ entries: [] })),
            adminApi.listHolidays().catch(() => ({ holidays: [] })),
            adminApi.listTemplates().catch(() => ({ templates: [] })),
            adminApi.getReport('dashboard', String(new Date().getFullYear())).catch(() => ({
              pendingCount: 0,
              upcomingLeave: [],
              alerts: [],
            })),
          ]);

        const users = usersRes.users || [];
        const types = typesRes.leaveTypes || [];
        const chains = chainsRes.chains || [];
        const holidays = holidaysRes.holidays || [];
        const templates = templatesRes.templates || [];

        setStats({
          users: users.filter((u) => u.isActive !== false).length,
          leaveTypes: types.filter((t) => t.isActive !== false).length,
          chains: chains.filter((c) => c.isActive !== false).length,
          pendingRequests: dashboardRes.pendingCount || 0,
          companyName: settingsRes.settings?.companyName || 'LeavePortal',
          hasUsers: users.length > 1,
          hasLeaveTypes: types.length > 0,
          hasChains: chains.length > 0,
          hasHolidays: holidays.length > 0,
          hasTemplates: templates.length > 0,
        });

        setRecentAudit((auditRes.entries || []).slice(0, 5));
        setAlerts((dashboardRes.alerts || []).slice(0, 5));
        setUpcomingLeave(dashboardRes.upcomingLeave || []);
      } catch (err) {
        toast.error(`Failed to load dashboard: ${err.message}`);
      } finally {
        setLoading(false);
      }

      // Load health check separately (non-blocking)
      adminApi.getHealthCheck().then((res) => setHealthIssues(res.issues || [])).catch(() => {});
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSeedAll() {
    setSeeding(true);
    try {
      await adminApi.seed(false);
      toast.success('Default data seeded (leave types, templates, approval chain)');
      window.location.reload();
    } catch (err) {
      toast.error(`Seed failed: ${err.message}`);
    } finally {
      setSeeding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <span className="ml-3 text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">{stats.companyName} &middot; Admin overview</p>
        </div>
        {!stats.hasLeaveTypes && (
          <Button variant="secondary" onClick={handleSeedAll} loading={seeding}>
            Seed sample data
          </Button>
        )}
      </div>

      {/* Stats cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active People" value={stats.users} link="/admin/users" color="#8238EB" />
        <StatCard label="Leave Types" value={stats.leaveTypes} link="/admin/leave-types" color="#15D2A4" />
        <StatCard label="Approval Chains" value={stats.chains} link="/admin/chains" color="#3985FD" />
        <StatCard label="Pending Requests" value={stats.pendingRequests} link="/admin/reports" color={stats.pendingRequests > 0 ? '#FFCA03' : 'hsl(var(--muted-foreground))'} />
      </div>

      {/* System Health */}
      {healthIssues.length > 0 && (
        <div className="mb-6">
          <Card className={healthIssues.some((i) => i.severity === 'critical') ? 'border-destructive/30' : 'border-warning/30'}>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                {healthIssues.some((i) => i.severity === 'critical') ? (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                ) : (
                  <Info className="h-4 w-4 text-warning" />
                )}
                <CardTitle className="text-base">System Health</CardTitle>
              </div>
              <div className="flex gap-2 text-xs">
                {healthIssues.filter((i) => i.severity === 'critical').length > 0 && (
                  <Badge variant="danger">{healthIssues.filter((i) => i.severity === 'critical').length} critical</Badge>
                )}
                {healthIssues.filter((i) => i.severity === 'warning').length > 0 && (
                  <Badge variant="warning">{healthIssues.filter((i) => i.severity === 'warning').length} warning{healthIssues.filter((i) => i.severity === 'warning').length !== 1 ? 's' : ''}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {healthIssues.map((issue, i) => (
                  <Link
                    key={i}
                    to={issue.link}
                    className="flex items-start gap-3 rounded-lg border border-border bg-muted/20 p-3 no-underline transition-colors hover:bg-muted/40"
                  >
                    {issue.severity === 'critical' ? (
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    ) : issue.severity === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                    ) : (
                      <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    )}
                    <div>
                      <div className="text-xs font-semibold text-foreground">{issue.message}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{issue.detail}</div>
                    </div>
                    <Badge variant="default" className="ml-auto shrink-0 text-[9px]">{issue.category}</Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Setup checklist */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Setup Checklist</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2.5">
            <ChecklistItem done={stats.hasUsers} label="Import or create users" link="/admin/users" />
            <ChecklistItem done={stats.hasLeaveTypes} label="Configure leave types" link="/admin/leave-types" />
            <ChecklistItem done={stats.hasHolidays} label="Add company holidays" link="/admin/holidays" />
            <ChecklistItem done={stats.hasChains} label="Set up approval chains" link="/admin/chains" />
            <ChecklistItem done={false} label="Review system settings" link="/admin/settings" />
            <ChecklistItem done={stats.hasTemplates} label="Customize email templates" link="/admin/templates" />
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <Link to="/admin/audit" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentAudit.length === 0 ? (
              <p className="text-xs text-muted-foreground">No recent activity.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {recentAudit.map((entry, i) => (
                  <div key={entry.rowKey || i} className={`flex items-center justify-between py-2 ${i < recentAudit.length - 1 ? 'border-b border-border' : ''}`}>
                    <div>
                      <span className="text-xs font-medium text-foreground">{formatAction(entry.action)}</span>
                      <span className="ml-2 text-xs text-muted-foreground">by {entry.actor || 'system'}</span>
                    </div>
                    <span className="whitespace-nowrap text-[11px] text-muted-foreground">{formatTime(entry.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team leave calendar */}
      <div className="mb-6">
        <TeamCalendar />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Upcoming leave */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upcoming Leave (Next 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingLeave.length === 0 ? (
              <p className="text-xs text-muted-foreground">No one is scheduled to be out.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {upcomingLeave.map((r, i) => (
                  <div key={i} className={`flex items-center justify-between py-2 ${i < upcomingLeave.length - 1 ? 'border-b border-border' : ''}`}>
                    <div>
                      <span className="text-xs font-medium text-foreground">{r.requesterName}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{r.typeName} &middot; {formatDays(r.days)}d</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{r.startDate}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance alerts */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Balance Alerts</CardTitle>
            <Link to="/admin/reports?type=alerts" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="text-xs text-muted-foreground">All balances are healthy.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {alerts.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between py-2 ${i < alerts.length - 1 ? 'border-b border-border' : ''}`}>
                    <div>
                      <span className="text-xs font-medium text-foreground">{a.displayName}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{a.typeName}</span>
                    </div>
                    <Badge variant={a.severity === 'critical' ? 'danger' : 'warning'}>
                      {formatDays(a.remaining)} days
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, link, color }) {
  const Icon = STAT_ICONS[label] || Users;
  return (
    <Link to={link} className="group no-underline">
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
        <CardContent className="flex items-center gap-4 p-5">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ background: color + '15' }}
          >
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold leading-tight text-foreground">{value}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function ChecklistItem({ done, label, link }) {
  return (
    <Link
      to={link}
      className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm text-foreground no-underline transition-all duration-150 hover:border-primary/30 hover:bg-accent"
    >
      {done ? (
        <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
      ) : (
        <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
      )}
      <span className={done ? 'text-muted-foreground line-through' : 'text-foreground'}>{label}</span>
    </Link>
  );
}

function TeamCalendar() {
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { year, month, monthLabel, daysInMonth, startDay } = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + monthOffset);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      monthLabel: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      daysInMonth: new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(),
      startDay: new Date(d.getFullYear(), d.getMonth(), 1).getDay(),
    };
  }, [monthOffset]);

  // Pull the same dataset the employee Team Calendar uses — approved leave,
  // WFH/Remote schedule entries, and holidays — so this admin widget shows
  // the full picture rather than just leave requests.
  useEffect(() => {
    setLoading(true);
    const ms = String(month + 1).padStart(2, '0');
    const sd = `${year}-${ms}-01`;
    const ed = `${year}-${ms}-${String(daysInMonth).padStart(2, '0')}`;
    employeeApi.getTeamCalendar(sd, ed)
      .then((res) => setData(res))
      .catch(() => setData({ leave: [], schedule: [], holidays: [] }))
      .finally(() => setLoading(false));
  }, [year, month, daysInMonth]);

  const holidayMap = useMemo(() => {
    const m = {};
    (data?.holidays || []).forEach((h) => { m[h.date] = h.name; });
    return m;
  }, [data?.holidays]);

  // Build a map: day number -> array of items for that day. Mirrors
  // TeamCalendarPage so the admin widget and the employee page agree.
  const dayMap = useMemo(() => {
    const map = {};
    if (!data) return map;
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const items = [];
      if (holidayMap[ds]) {
        items.push({ kind: 'holiday', label: holidayMap[ds], color: '#F85707' });
      }
      for (const l of data.leave || []) {
        if (l.startDate <= ds && l.endDate >= ds && l.status === 'approved') {
          items.push({
            kind: 'leave',
            label: l.userName?.split(' ')[0] || 'Employee',
            color: l.typeColor || '#3985FD',
          });
        }
      }
      for (const s of data.schedule || []) {
        if (s.date === ds) {
          items.push({
            kind: s.type,
            label: s.userName?.split(' ')[0] || 'Employee',
            color: s.type === 'wfh' ? '#15D2A4' : '#00AFDC',
          });
        }
      }
      map[d] = items;
    }
    return map;
  }, [data, daysInMonth, year, month, holidayMap]);

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Hover popover for busy days — same UX as the employee Team Calendar.
  const [hoveredDay, setHoveredDay] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0, side: 'bottom' });

  const handleDayEnter = (day, e) => {
    const items = dayMap[day] || [];
    if (items.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const side = spaceBelow >= popoverHeight + 16 ? 'bottom' : 'top';
    const popoverWidth = 320;
    let x = rect.left + rect.width / 2;
    const halfW = popoverWidth / 2;
    x = Math.max(halfW + 12, Math.min(window.innerWidth - halfW - 12, x));
    const y = side === 'bottom' ? rect.bottom + 8 : rect.top - 8;
    setHoverPos({ x, y, side });
    setHoveredDay(day);
  };
  const handleDayLeave = () => setHoveredDay(null);

  const hoveredItems = hoveredDay ? (dayMap[hoveredDay] || []) : [];
  const hoveredDateStr = hoveredDay
    ? `${year}-${String(month + 1).padStart(2, '0')}-${String(hoveredDay).padStart(2, '0')}`
    : null;
  const hoveredDayName = hoveredDay
    ? fullDayNames[new Date(year, month, hoveredDay).getDay()]
    : null;
  const hoveredHoliday = hoveredDateStr ? holidayMap[hoveredDateStr] : null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Team Leave Calendar</CardTitle>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMonthOffset((o) => o - 1)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[140px] text-center text-sm font-medium">{monthLabel}</span>
          <button
            onClick={() => setMonthOffset((o) => o + 1)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
            {/* Day headers */}
            {dayNames.map((d) => (
              <div key={d} className="bg-muted/50 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {d}
              </div>
            ))}
            {/* Empty cells before month starts */}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[60px] bg-card/50" />
            ))}
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const people = dayMap[day] || [];
              const isToday = isCurrentMonth && day === todayDate;
              const isWeekend = (startDay + i) % 7 === 0 || (startDay + i) % 7 === 6;

              return (
                <div
                  key={day}
                  onMouseEnter={(e) => handleDayEnter(day, e)}
                  onMouseLeave={handleDayLeave}
                  className={`min-h-[60px] p-1 ${isWeekend ? 'bg-card/30' : 'bg-card'} ${isToday ? 'ring-1 ring-inset ring-primary' : ''}`}
                >
                  <div className={`text-[11px] font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                    {day}
                  </div>
                  <div className="mt-0.5 flex flex-col gap-px">
                    {people.slice(0, 2).map((p, pi) => (
                      <div
                        key={pi}
                        className="truncate rounded px-1 py-px text-[9px] font-medium"
                        style={{ background: (p.color || '#3985fd') + '25', color: p.color || '#3985fd' }}
                      >
                        {p.label}
                      </div>
                    ))}
                    {people.length > 2 && (
                      <div className="px-1 text-[9px] text-muted-foreground">+{people.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      {hoveredDay && hoveredItems.length > 0 && (
        <div
          className="pointer-events-none fixed z-50 w-80 rounded-lg border border-border bg-popover p-4 shadow-2xl"
          style={{
            left: hoverPos.x,
            top: hoverPos.y,
            transform: hoverPos.side === 'bottom' ? 'translateX(-50%)' : 'translate(-50%, -100%)',
          }}
        >
          <div className="mb-3 flex items-baseline justify-between border-b border-border pb-2">
            <div>
              <div className="text-lg font-bold text-foreground leading-none">
                {hoveredDayName}, {new Date(year, month, hoveredDay).toLocaleString('en-US', { month: 'short' })} {hoveredDay}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {hoveredItems.length} {hoveredItems.length === 1 ? 'event' : 'events'}
              </div>
            </div>
            {isCurrentMonth && hoveredDay === todayDate && (
              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Today</span>
            )}
          </div>

          {hoveredHoliday && (
            <div
              className="mb-2 rounded-md px-2 py-1.5 text-xs font-semibold"
              style={{ background: '#F8570718', color: '#F85707' }}
            >
              {hoveredHoliday}
            </div>
          )}

          <div className="max-h-[260px] space-y-1 overflow-y-auto">
            {hoveredItems.filter((it) => it.kind !== 'holiday').map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs"
                style={{ background: (item.color || '#3985fd') + '18', color: item.color || '#3985fd' }}
              >
                {item.kind === 'wfh' && <Home className="h-3 w-3 shrink-0" />}
                {item.kind === 'remote' && <Globe className="h-3 w-3 shrink-0" />}
                <span className="font-semibold">{item.label}</span>
                {item.kind === 'wfh' && (
                  <span className="ml-auto text-[9px] uppercase tracking-wide opacity-60">WFH</span>
                )}
                {item.kind === 'remote' && (
                  <span className="ml-auto text-[9px] uppercase tracking-wide opacity-60">Remote</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function formatAction(action) {
  if (!action) return '';
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const diffMins = Math.floor((Date.now() - d) / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ts; }
}
